'use client';

import { Phone } from 'lucide-react';

import { Button } from '@/shared/ui';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';
import { useStaffCallStore } from '../hooks/useStaffCallClient';

interface CallGuestButtonProps {
  /**
   * The guest's MARKETPLACE id (`guest_users.id`), not the per-hotel
   * `guests.id`.
   *
   * The gateway authorises an outbound call against `guest_hotels`, which keys
   * on this. Null for a walk-in created at the desk who never used BookMyRoom
   * — they have no app to ring, and the button hides rather than offering a
   * call that would always be refused.
   */
  guestUserId: string | null | undefined;
  /** Shown while the call is dialling, before the gateway confirms. */
  guestName: string;
  bookingId?: string;
  /** The staff member placing the call — their own id, from the session. */
  staffId: string;
  className?: string;
  id?: string;
}

/**
 * Ring a guest in the BookMyRoom app.
 *
 * The mirror of BookMyRoom's `CallButton`, and it follows the same rule: the
 * button refuses locally when it has nothing valid to send, rather than firing
 * a request the server will reject. A guest with no `guestUserId` has no app
 * account, so there is no device to ring — offering the control and then
 * failing would read as a broken feature rather than an absent one.
 *
 * Everything else is the server's decision. Whether the guest is online, is
 * already on a call, or still holds a booking here is resolved by the gateway
 * and reported through `call:rejected`; guessing any of it here would be a
 * second source of truth that drifts.
 */
export function CallGuestButton({
  guestUserId,
  guestName,
  bookingId,
  staffId,
  className,
  id,
}: CallGuestButtonProps) {
  const { t } = useTranslation();
  const startOutboundCall = useStaffCallStore((s) => s.startOutboundCall);
  const callState = useStaffCallStore((s) => s.state);

  // No app account — nothing to ring.
  if (!guestUserId) return null;

  // One line per staff member: the store refuses a second concurrent call, so
  // the button reflects that rather than appearing to work and doing nothing.
  const busy = callState !== 'idle' && callState !== 'ended';

  return (
    <Button
      id={id}
      variant="ghost"
      size="sm"
      disabled={busy}
      title={
        busy
          ? t('callsPage.alreadyOnACall')
          : t('callsPage.callGuestNamed', { name: guestName })
      }
      aria-label={t('callsPage.callGuestNamed', { name: guestName })}
      leftIcon={<Phone className="h-3.5 w-3.5" />}
      className={cn(className)}
      onClick={() => startOutboundCall(staffId, { guestUserId, name: guestName, bookingId })}
    >
      {t('callsPage.call')}
    </Button>
  );
}
